import { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { ChevronRight, CheckCircle, XCircle, Mail, Loader2 } from 'lucide-react';
import { api } from './lib/api';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';

function App() {
  const { user, loading, error, login, signup, logout, pendingAccounts, selectAccount } = useAuth();

  // URL action state
  const [urlAction, setUrlAction] = useState<string | null>(null);
  const [urlToken, setUrlToken] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reset password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Email verification banner
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const token = params.get('token');

    if (action && token) {
      setUrlAction(action);
      setUrlToken(token);

      if (action === 'verify-email') {
        handleVerifyEmail(token);
      }
    }
  }, []);

  const handleVerifyEmail = async (token: string) => {
    setActionLoading(true);
    try {
      const result = await api.verifyEmail(token);
      setActionMessage(result.message);
    } catch (err: any) {
      setActionError(err.message || 'Failed to verify email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlToken) return;

    if (newPassword.length < 8) {
      setActionError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setActionError('Passwords do not match');
      return;
    }

    setActionLoading(true);
    setActionError(null);
    try {
      const result = await api.resetPassword(urlToken, newPassword);
      setActionMessage(result.message);
    } catch (err: any) {
      setActionError(err.message || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const clearAction = () => {
    setUrlAction(null);
    setUrlToken(null);
    setActionMessage(null);
    setActionError(null);
    setNewPassword('');
    setConfirmPassword('');
    // Clean URL
    window.history.replaceState({}, '', window.location.pathname);
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await api.resendVerification();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch {
      // Silently fail
    } finally {
      setResending(false);
    }
  };

  // ─── Verify Email Action ──────────────────────────────
  if (urlAction === 'verify-email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          {actionLoading ? (
            <>
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900">Verifying your email...</h2>
            </>
          ) : actionMessage ? (
            <>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-6">{actionMessage}</p>
              <button
                onClick={clearAction}
                className="bg-blue-600 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </>
          ) : (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h2>
              <p className="text-red-600 mb-6">{actionError}</p>
              <button
                onClick={clearAction}
                className="bg-blue-600 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Reset Password Action ────────────────────────────
  if (urlAction === 'reset-password') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Set new password</h2>
            <p className="text-gray-600 text-sm mt-1">Choose a strong password to protect your account</p>
          </div>

          {actionMessage ? (
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Password Reset Successfully!</h3>
              <p className="text-gray-600 text-sm mb-6">{actionMessage}</p>
              <button
                onClick={clearAction}
                className="bg-blue-600 text-white py-2.5 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {actionError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{actionError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Re-enter your password"
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                disabled={actionLoading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {actionLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Resetting...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </button>

              <button
                type="button"
                onClick={clearAction}
                className="w-full text-sm text-gray-600 hover:text-gray-800 py-2"
              >
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ─── Loading ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  // ─── Account Selection ────────────────────────────────
  if (!user && pendingAccounts) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border p-6 w-full max-w-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Select Account</h2>
          <p className="text-sm text-gray-500 mb-4">You belong to multiple organizations. Choose one to continue.</p>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="space-y-2">
            {pendingAccounts.map(account => (
              <button
                key={account.customer_id}
                onClick={() => selectAccount(account.customer_id)}
                disabled={loading}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">{account.company_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{account.role}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Not Logged In ────────────────────────────────────
  if (!user) {
    return <Auth onLogin={login} onSignup={signup} />;
  }

  // ─── Logged In (with email verification banner) ───────
  return (
    <>
      {user.email_verified === false && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
          <Mail className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-amber-800">
            Please verify your email address. Check your inbox for a verification link.
          </span>
          <button
            onClick={handleResendVerification}
            disabled={resending || resendSuccess}
            className="text-amber-700 font-medium hover:text-amber-900 disabled:opacity-50 whitespace-nowrap"
          >
            {resending ? 'Sending...' : resendSuccess ? 'Sent!' : 'Resend email'}
          </button>
        </div>
      )}
      <Dashboard user={user} onLogout={logout} />
    </>
  );
}

export default App;
